import React from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  Building2,
  FactoryIcon,
  FileStack,
  Wallet,
  MapPinned,
  Network,
  ArrowUpRight,
  ArrowRight,
  ArrowUp,
  ChevronRight,
} from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";

// ---------- Solution-pathway slug map (for "Related Solution Pathways") ----------
const PATHWAY_SLUG = {
  "Universities, Hospitals, Chambers, Unions & Multi-Department Institutions":
    "structured-institutions",
  "Municipalities & Local Public Administrations": "municipality",
  "Industrial Zones, Utilities & Service Operators": "industrial-zone",
  "Document-Intensive & Regulated Service Environments": "electronic-document",
  "Finance, Revenue & Administrative Operations": "finance-revenue",
  "Field, Facility, Laboratory & Technical Operations": "field-infrastructure",
  "Portals, Integrations & Executive Visibility": "portals-integrations",
};

// ---------- Portfolio data ----------
const PORTFOLIO = [
  {
    id: "family-a",
    anchor: "institutional-management-platforms",
    letter: "A",
    icon: Layers,
    title: "Institutional Management Platforms",
    overview:
      "Core management information systems, public administration platforms, executive dashboards, service definitions, meeting management, quality management, and strategic planning structures.",
    short:
      "Core platforms for institutions that need structured management, administrative visibility, service definitions, reporting, and strategic coordination across departments and workflows.",
    related: [
      "Universities, Hospitals, Chambers, Unions & Multi-Department Institutions",
      "Finance, Revenue & Administrative Operations",
      "Portals, Integrations & Executive Visibility",
    ],
    modules: [
      ["Management Information System", "Institutional management environment for coordinating administrative, operational, reporting, and service-related processes through a structured digital platform."],
      ["Public Administration Management Information System", "A management information system designed for public administrations and service-oriented institutions that need integrated operational control and administrative visibility."],
      ["Executive Management Dashboard", "Dashboard and reporting environment for management-level visibility, monitoring, institutional performance review, and decision-support structures."],
      ["Institutional Web Portal", "A structured internal portal environment for institutional users, services, workflows, information access, and role-based digital coordination."],
      ["General Parameter Management", "Configuration and parameter management layer for defining institutional rules, settings, service logic, operational categories, and system behavior."],
      ["Service Definition Management", "Module for defining, organizing, and managing institutional services, service categories, process descriptions, and administrative service structures."],
      ["Meeting Management", "Digital structure for organizing meetings, agendas, decisions, follow-ups, responsibilities, and institutional meeting records."],
      ["Quality Management System", "Management layer for quality processes, documentation, corrective actions, monitoring, and structured institutional improvement workflows."],
      ["Strategic Planning Management", "Module for strategic planning, institutional objectives, performance indicators, implementation monitoring, and management reporting."],
    ],
  },
  {
    id: "family-b",
    anchor: "municipality-public-service-systems",
    letter: "B",
    icon: Building2,
    title: "Municipality & Public Service Systems",
    overview:
      "Digital modules for municipal services, citizen interaction, public service desks, municipal departments, service coordination, and public-facing operations.",
    short:
      "Digital management modules for municipalities and public-service organizations that manage citizen services, municipal departments, service requests, public operations, field services, and administrative workflows.",
    related: [
      "Municipalities & Local Public Administrations",
      "Document-Intensive & Regulated Service Environments",
      "Field, Facility, Laboratory & Technical Operations",
      "Portals, Integrations & Executive Visibility",
    ],
    modules: [
      ["E-Municipality Portal", "Digital municipal service portal for public-facing service access, applications, information, and citizen-oriented digital interaction where applicable."],
      ["Citizen Relationship Management / Public Service Desk", "System for managing citizen requests, complaints, service applications, communication records, follow-up processes, and public service desk workflows."],
      ["Municipal Police Management", "Operational module for municipal police processes, inspections, records, field activities, and administrative follow-up."],
      ["Fire Department Management", "Module for fire department records, incidents, operational follow-up, service requests, reporting, and emergency-related administrative processes."],
      ["Municipal Health Services Management", "Management structure for municipal health service processes, service records, applications, follow-up, and reporting."],
      ["Nursery / Daycare Services Management", "Module for municipal nursery or daycare service management, applications, records, service tracking, and administrative coordination."],
      ["Cultural Services Management", "System for managing cultural services, events, programs, applications, venues, and related municipal cultural activities."],
      ["Library Management", "Module for library records, users, resources, borrowing processes, service tracking, and reporting."],
      ["Sports Services Management", "System for sports services, facilities, programs, applications, reservations, and municipal sports-related activities."],
      ["Transportation Services Management", "Module for municipal transportation-related processes, service coordination, records, and operational follow-up."],
      ["Cleaning Services Management", "System for cleaning service planning, records, field coordination, service tracking, and operational reporting."],
      ["Parks and Gardens Management", "Module for park, garden, green area, maintenance, landscaping, and related field-service coordination."],
      ["Veterinary Services Management", "System for veterinary services, animal-related municipal processes, records, applications, and follow-up."],
      ["Burial Services Management", "Module for burial service processes, applications, scheduling, records, and administrative coordination."],
      ["Cemetery Information System", "Digital information system for cemetery records, burial places, maps or registry-related data, applications, and searchability where applicable."],
      ["Marriage Services Management", "Module for marriage application processes, scheduling, document control, records, and administrative follow-up."],
      ["Public Bread Distribution Management", "System for managing public bread production, distribution, service points, records, and related operational processes where applicable."],
      ["Wholesale Market Management", "Module for wholesale market operations, trader records, transactions, service processes, and administrative management."],
      ["Press and Publications Management", "System for press, publication, announcement, media, and communication-related municipal processes."],
      ["Appointment Management", "Scheduling and appointment workflow capability for institutional services, citizen-facing appointments, internal service appointments, queues, and related service coordination. Availability and exact workflow scope may depend on local service rules, identity mechanisms, payment requirements, and institutional process design."],
    ],
  },
  {
    id: "family-c",
    anchor: "industrial-zone-utility-management",
    letter: "C",
    icon: FactoryIcon,
    title: "Industrial Zone & Utility Management",
    overview:
      "Systems for organized industrial zones, utility subscribers, electricity, water, natural gas, industrialist portals, meter reading, payment points, and field operations.",
    short:
      "Operational systems for organized industrial zones, utility-based institutional environments, subscribers, infrastructure services, industrialist portals, meter reading, payment workflows, and field operations.",
    related: [
      "Industrial Zones, Utilities & Service Operators",
      "Field, Facility, Laboratory & Technical Operations",
      "Finance, Revenue & Administrative Operations",
      "Portals, Integrations & Executive Visibility",
    ],
    modules: [
      ["Industrial Zone Management Information System", "Integrated management information system for organized industrial zones, covering administrative, operational, utility, subscriber, document, service, and reporting needs."],
      ["Utility Subscriber Management System", "Subscriber management structure for electricity, water, natural gas, and other utility-related institutional services, including subscriber records, consumption, accrual, collection, and service workflows."],
      ["Electricity Service Management Module", "Module for electricity service processes, subscriber records, consumption data, billing-related workflows, and utility service coordination."],
      ["Water Service Management Module", "Module for water service processes, subscriber records, consumption data, billing-related workflows, and water service coordination."],
      ["Natural Gas Service Management Module", "Module for natural gas service processes, subscriber records, consumption data, billing-related workflows, and gas service coordination."],
      ["Industrialist / Tenant Self-Service Portal", "Self-service portal for industrial zone participants, industrialists, tenants, or similar users to access information, applications, service records, requests, and account-related workflows."],
      ["Prepaid Meter Management", "Module for prepaid meter operations, subscriber balance logic, consumption tracking, and meter-related process management where infrastructure allows."],
      ["Automated Meter Reading System Integration", "Integration and compatibility layer for automated meter reading systems where AMR / AMI infrastructure, compatible devices, or vendor APIs are available. Availability depends on meter infrastructure, third-party systems, vendor access, and local utility model."],
      ["Meter Index Reading", "Module for recording, managing, validating, and following up meter index readings."],
      ["Mobile Meter Reading", "Mobile field module for reading meters, recording index data, and transmitting field information into the institutional system."],
      ["Mobile Work Order Management", "Mobile work order structure for assigning, tracking, and completing field tasks, maintenance jobs, subscriber-related work, and operational interventions."],
      ["Payment Points Management", "Module for managing payment points, collection locations, payment records, and related payment workflows."],
      ["Direct Debit System", "Direct debit readiness for structured collection workflows where banking infrastructure, legal framework, and third-party access allow."],
      ["Automated Payment Integration", "Payment integration readiness for automated payment channels, banking connections, online collection, or external payment services where available."],
    ],
  },
  {
    id: "family-d",
    anchor: "document-records-digital-transformation",
    letter: "D",
    icon: FileStack,
    title: "Document, Records & Digital Transformation",
    overview:
      "Electronic document management, digital archive, document verification, e-signature readiness, registered electronic mail readiness, e-invoice, e-ledger, e-archive, archive scanning, and document tracking.",
    short:
      "Systems for electronic document management, digital archives, institutional records, document routing, authorization, traceability, digital transformation readiness, and long-term information continuity.",
    related: [
      "Document-Intensive & Regulated Service Environments",
      "Universities, Hospitals, Chambers, Unions & Multi-Department Institutions",
      "Municipalities & Local Public Administrations",
      "Finance, Revenue & Administrative Operations",
    ],
    modules: [
      ["Electronic Document Management System", "System for managing electronic documents, institutional correspondence, approvals, routing, authorization, document history, traceability, and official document workflows."],
      ["Digital Archive Management", "Digital archive structure for storing, classifying, searching, retrieving, and managing institutional records and supporting documents."],
      ["Document Management System", "Document management environment for organizing files, versions, classifications, access rights, workflows, and institutional document continuity."],
      ["Document Verification", "Capability for verifying document authenticity, document status, document references, or electronic document validity where the relevant infrastructure and rules allow."],
      ["E-Archive Integration", "E-archive integration readiness for digital archiving, electronic recordkeeping, and country-specific e-transformation processes where legally and technically available."],
      ["E-Invoice Integration", "E-invoice integration readiness for electronic invoicing processes where legally and technically available, depending on the country’s legal, tax, and technical infrastructure."],
      ["E-Ledger Integration", "E-ledger integration readiness for electronic ledger processes where legally and technically available, subject to local tax legislation, accounting rules, and certified systems."],
      ["Electronic Signature", "Electronic signature readiness for document approvals, workflows, and institutional processes where legally and technically available, subject to recognized e-signature infrastructure and providers."],
      ["Registered Electronic Mail Integration", "Registered electronic mail integration readiness for legally traceable electronic communication systems where such infrastructure exists and is legally available."],
      ["Archive Scanning / Archive Digitization", "Service and system capability for converting physical archives into digital records, including scanning, indexing, classification, and structured retrieval."],
      ["Document Tracking", "Module for tracking document movement, status, routing, responsibility, deadlines, approvals, and institutional document lifecycle."],
      ["Timestamp Support", "Timestamp support for electronic records, signatures, or document events where legally and technically available."],
    ],
  },
  {
    id: "family-e",
    anchor: "finance-revenue-procurement",
    letter: "E",
    icon: Wallet,
    title: "Finance, Revenue & Procurement",
    overview:
      "Revenue, accrual, collection, accounting, procurement, inventory, movable asset management, vehicle management, online collection, and financial process integration.",
    short:
      "Financial and administrative modules for revenue, accrual, collection, accounting, procurement, inventory, movable assets, vehicles, online payment readiness, and financial process visibility.",
    related: [
      "Finance, Revenue & Administrative Operations",
      "Municipalities & Local Public Administrations",
      "Industrial Zones, Utilities & Service Operators",
      "Universities, Hospitals, Chambers, Unions & Multi-Department Institutions",
    ],
    modules: [
      ["Revenue Management", "Module for managing institutional revenue processes, revenue categories, accruals, collections, account follow-up, and reporting."],
      ["Revenue and Accrual Management", "Structure for accrual-based revenue processes, assessment, billing logic, account records, and collection follow-up."],
      ["Accounting Management", "Accounting workflow structure for institutional accounting processes, records, financial transactions, and reporting."],
      ["Analytical Accounting", "Analytical accounting structure for classifying, monitoring, and reporting financial information according to institutional categories, programs, or management needs."],
      ["Procurement and Direct Purchasing Management", "Module for procurement, direct purchasing, supplier records, requests, approvals, purchasing workflows, and administrative follow-up."],
      ["Inventory Management", "System for inventory records, stock movements, warehouse processes, item tracking, and institutional inventory visibility."],
      ["Movable Asset Management", "Module for movable asset records, custody, assignment, movement, depreciation-related tracking where applicable, and institutional asset visibility."],
      ["Movable Asset Inventory Count", "Module for counting, verifying, reconciling, and reporting movable assets within institutional inventory processes."],
      ["Vehicle Management", "Module for vehicle records, fleet information, usage tracking, maintenance records, assignments, and operational follow-up."],
      ["E-Invoice", "Electronic invoice readiness for countries and institutions where e-invoice legislation, tax systems, and technical providers support implementation, where legally and technically available."],
      ["E-Ledger", "Electronic ledger readiness for jurisdictions and institutions where e-ledger rules, accounting frameworks, and certified infrastructures are available, where legally and technically available."],
      ["E-Archive", "Electronic archive readiness for structured storage of relevant digital financial and administrative records where legally and technically available."],
      ["Online Payment Collection", "Online payment and collection readiness for institutional receivables where payment infrastructure, banking systems, and legal framework allow."],
      ["Fast Payment Collection", "Fast collection workflow capability for relevant institutional payment processes, subject to available payment channels and system integration."],
      ["Field Research for Revenue Enhancement", "Field data collection and research capability to support revenue improvement, data correction, service identification, and institutional revenue visibility."],
      ["Payroll and Human Resources Management", "Module for personnel records, HR-related workflows, payroll-related process support, staff administration, and institutional employee data management. Payroll, tax, social security, employment law, and reporting requirements must be localized before use in any specific country."],
    ],
  },
  {
    id: "family-f",
    anchor: "field-infrastructure-spatial-operations",
    letter: "F",
    icon: MapPinned,
    title: "Field, Infrastructure & Spatial Operations",
    overview:
      "Zoning, address numbering, licensing, real estate records, land registry readiness, GIS readiness, maintenance, mobile work orders, laboratory processes, and field data collection.",
    short:
      "Modules for zoning, address numbering, licensing, real estate records, land registry readiness, GIS readiness, fault management, maintenance, mobile work execution, laboratory workflows, wastewater analysis, and field data collection.",
    related: [
      "Field, Facility, Laboratory & Technical Operations",
      "Municipalities & Local Public Administrations",
      "Industrial Zones, Utilities & Service Operators",
      "Document-Intensive & Regulated Service Environments",
    ],
    modules: [
      ["Zoning and Address Numbering Management", "Module for zoning, address numbering, parcel-related administrative processes, address records, spatial references, and institutional follow-up."],
      ["Licensing and Permit Management", "System for license and permit applications, review, approvals, documentation, status tracking, and administrative management."],
      ["Real Estate / Immovable Property Management", "Module for real estate or immovable property records, property-related workflows, institutional property data, and administrative follow-up."],
      ["Land Registry Integration", "Land registry integration readiness for property-related data exchange where legal permission, technical access, and public infrastructure allow."],
      ["Land Registry and Cadastre Information System Integration", "Integration readiness for land registry and cadastre information systems where the relevant public infrastructure and legal access model are available."],
      ["Spatial Address Registration System Integration", "Integration readiness for spatial address registration systems where official address infrastructure, legal permission, and technical access allow."],
      ["Spatial Real Estate System Integration", "Integration readiness for spatial real estate systems where such public or institutional systems are available."],
      ["Address Numbering Services", "Administrative and field-service capability for address numbering, address correction, field verification, and institutional address records."],
      ["Geographic Information System Integration", "Geographic information system integration readiness for map-based, parcel-based, infrastructure-based, or spatial workflows where GIS infrastructure is available."],
      ["Fault / Incident Management", "Module for recording, assigning, tracking, and resolving faults, incidents, field requests, and operational problems."],
      ["Maintenance and Repair Management", "System for maintenance planning, repair records, work orders, field teams, recurring maintenance, and operational follow-up."],
      ["Mobile Work Order Management", "Mobile field capability for assigning, updating, completing, and reporting institutional work orders from the field."],
      ["Data Reading Services", "Field data collection and data reading capability for operational, subscriber, infrastructure, meter, or institutional records."],
      ["Laboratory Management", "Module for laboratory records, analysis workflows, sample tracking, results, reporting, and operational follow-up."],
      ["Wastewater Discharge and Analysis Management", "Module for wastewater discharge records, analysis processes, monitoring, reporting, and related environmental or utility workflows."],
      ["Civil Defense Management", "Module for civil defense, emergency preparedness records, institutional safety processes, planning records, and related operational follow-up. Scope depends on local public safety responsibilities, institutional authority, and emergency management rules."],
      ["Private Security Management", "Module for private security service records, assignments, personnel-related operational workflows, incidents, patrol logic, and institutional security follow-up. Security operations, personnel rules, licensing, and compliance requirements must be reviewed according to the local legal framework."],
      ["Administrative Affairs Management", "Module for internal administrative affairs, institutional support requests, internal service records, office operations, facilities-related coordination, and support process tracking. Generally adaptable, subject to local workflow design and institutional operating model."],
    ],
  },
  {
    id: "family-g",
    anchor: "portals-integrations-external-systems",
    letter: "G",
    icon: Network,
    title: "Portals, Integrations & External Systems",
    overview:
      "Institutional portals, mobile applications, call center integration, SMS, identity and address systems, e-government readiness, payment systems, direct debit, single sign-on, and external communication.",
    short:
      "Portal structures, mobile access, external communication, identity and address integration readiness, e-government readiness, payment system readiness, direct debit, single sign-on, and external system communication.",
    related: [
      "Portals, Integrations & Executive Visibility",
      "Municipalities & Local Public Administrations",
      "Industrial Zones, Utilities & Service Operators",
      "Universities, Hospitals, Chambers, Unions & Multi-Department Institutions",
    ],
    modules: [
      ["Institutional Web Portal", "Portal environment for institutional users, internal services, information access, workflow visibility, and role-based digital interaction."],
      ["Industrialist / Tenant Portal", "Portal for industrialists, tenants, organized zone participants, or similar users to access service information, applications, account data, requests, and process status."],
      ["Mobile Management Information System", "Mobile access layer for management information, dashboards, notifications, approvals, and selected institutional workflows."],
      ["Mobile Citizen Application", "Mobile application readiness for citizen-facing services, requests, applications, notifications, and public-service interaction."],
      ["Call Center Integration", "Integration readiness for call center systems, service records, requests, communication logs, and citizen or user interaction workflows."],
      ["SMS Notification Integration", "SMS notification capability for alerts, reminders, service updates, payment information, approvals, and institutional communication."],
      ["Identity Data Sharing System Integration", "Identity data integration readiness for official identity or population data services where such public infrastructure and legal access are available."],
      ["Spatial Address Registration System Integration", "Integration readiness for official spatial address systems, address registries, or similar public data sources where available."],
      ["State Organization Central Registration System Integration", "Integration readiness for central public organization registry systems where such systems exist and technical access is permitted."],
      ["E-Government Integration", "E-government integration readiness for public services, portals, data exchange, applications, or official workflows where local e-government infrastructure allows."],
      ["Electronic Correspondence Package Integration", "Integration readiness for electronic correspondence package standards or official digital communication formats where applicable."],
      ["Public Personnel Service Tracking Integration", "Integration readiness for public personnel or service tracking systems where relevant official systems are available."],
      ["Online Data Transfer System Integration", "Integration readiness for online data transfer systems, reporting channels, or public authority data exchange structures where supported."],
      ["Direct Debit System Integration", "Integration readiness for direct debit workflows through banking or payment infrastructures where legal and technical access allows."],
      ["Payment Point Integration", "Integration readiness for external payment points, collection offices, banking channels, or payment service providers."],
      ["Automated Payment Integration", "Integration readiness for automated payment collection, payment matching, and bank or payment service communication where infrastructure allows."],
      ["Single Sign-On Architecture", "Single sign-on capability for centralized authentication, user access management, and role-based login structures where technically appropriate."],
      ["LDAP Directory Integration", "Integration with LDAP and directory services for identity, user management, access control, and institutional authentication environments. Availability depends on the customer’s identity infrastructure, directory environment, and security requirements."],
      ["REST & SOAP Web Services", "Service-oriented integration layer using REST and SOAP web services for internal and external system communication. Actual integration depends on available APIs, public authority systems, customer systems, documentation quality, security rules, and third-party access permissions."],
    ],
  },
];

// ---------- Components ----------
const FamilyOverviewCard = ({ f }) => (
  <a
    href={`#${f.anchor}`}
    data-testid={`portfolio-overview-${f.id}`}
    className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col group"
  >
    <div className="flex items-start justify-between">
      <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
        <f.icon size={18} strokeWidth={1.5} />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
        {f.letter}
      </div>
    </div>
    <h3 className="font-display text-lg sm:text-xl font-semibold text-ink-900 tracking-tight mt-5">
      {f.title}
    </h3>
    <p className="mt-3 text-[14px] leading-relaxed text-ink-600 flex-1">{f.overview}</p>
    <span className="mt-5 inline-flex items-center gap-1 text-[12px] font-medium text-cyan-700 uppercase tracking-widest group-hover:text-cyan-800 transition-colors">
      Browse modules <ChevronRight size={12} />
    </span>
  </a>
);

const FamilySection = ({ f, index }) => (
  <section
    id={f.anchor}
    data-testid={`portfolio-${f.id}`}
    className={`scroll-mt-24 border-t border-ink-200 ${index % 2 === 0 ? "bg-white" : "bg-ink-50"}`}
  >
    {/* Backwards-compatible legacy anchor (e.g. #family-a) */}
    <span id={f.id} className="block relative -top-24" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
            Family {f.letter}
          </div>
          <div className="mt-4 w-12 h-12 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
            <f.icon size={20} strokeWidth={1.5} />
          </div>
          <h2 className="heading-display mt-5 text-2xl sm:text-3xl lg:text-4xl font-semibold">
            {f.letter}. {f.title}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-600">{f.short}</p>

          {/* Related Solution Pathways — contextual visual tags (non-clickable) */}
          {f.related && f.related.length > 0 && (
            <div className="mt-8" data-testid={`related-pathways-${f.id}`}>
              <div className="tag-overline">Related Solution Pathways</div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {f.related.map((label) => {
                  const slug = PATHWAY_SLUG[label];
                  if (!slug) return null;
                  return (
                    <li key={label}>
                      <span
                        data-testid={`related-pathway-${f.id}-${slug}`}
                        className="inline-flex items-center gap-1 text-[12px] text-ink-600 bg-ink-50 border border-ink-200 px-3 py-1.5 cursor-default select-none"
                      >
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <div className="lg:col-span-8">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {f.modules.map(([name, desc], i) => (
              <li
                key={name}
                data-testid={`portfolio-${f.id}-module-${i + 1}`}
                className="bg-white border border-ink-200 p-5 sm:p-6 card-hover flex flex-col"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-2 w-1.5 h-1.5 bg-cyan-500 shrink-0"
                    aria-hidden="true"
                  />
                  <div>
                    <h3 className="font-display text-[15.5px] font-semibold text-ink-900 leading-snug tracking-tight">
                      {name}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-ink-600">{desc}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex justify-end">
            <a
              href="#portfolio-overview"
              data-testid={`portfolio-${f.id}-back-to-overview`}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-500 hover:text-cyan-700 uppercase tracking-widest transition-colors"
            >
              <ArrowUp size={12} /> Back to Portfolio Overview
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Page ----------
const ProductPortfolio = () => (
  <main data-testid="page-product-portfolio">
    <PageHero
      overline="Product Portfolio"
      title="A modular institutional systems portfolio for complex organizations."
      lead="Allinstra’s Product Portfolio presents the complete institutional product and module architecture behind the solution pathways."
    >
      <div className="max-w-3xl space-y-4 text-[15px] text-ink-600 leading-relaxed">
        <p>
          While Solutions are organized around institution types, operational scenarios, and
          practical use cases, Product Portfolio is organized by product family and module
          structure. This page is designed as the full reference layer for institutions that
          want to understand the available system capabilities in detail.
        </p>
        <p>
          The portfolio is modular and implementation-aware. Modules may be evaluated
          independently or combined across product families according to institutional
          priorities, local legal requirements, public infrastructure, integration feasibility,
          and phased implementation needs.
        </p>
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/contact" data-testid="portfolio-hero-contact" className="btn-primary">
          Discuss Your Institution <ArrowUpRight size={16} />
        </Link>
        <Link to="/solutions" data-testid="portfolio-hero-solutions" className="btn-secondary">
          Explore Solutions <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    {/* Overview */}
    <SectionWrapper id="portfolio-overview">
      <SectionHeader
        overline="Portfolio Overview"
        title="Seven product families. One institutional structure."
        lead="Allinstra groups its institutional system capabilities into seven clear families. This structure makes the portfolio easier to understand, easier to scope, and easier to adapt to different markets and institutions."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PORTFOLIO.map((f) => (
          <FamilyOverviewCard key={f.id} f={f} />
        ))}
        {/* Modular summary card balances the 7-family grid and reinforces positioning */}
        <div
          data-testid="portfolio-modular-summary"
          className="hidden lg:flex bg-ink-50 border border-ink-200 p-7 sm:p-8 items-end card-hover"
        >
          <div>
            <div className="tag-overline">Modular & implementation-aware</div>
            <div className="font-display text-[22px] sm:text-2xl font-semibold text-ink-900 mt-2 tracking-tight leading-snug">
              A modular institutional portfolio across 7 product families.
            </div>
            <div className="text-xs text-ink-500 mt-2">
              Scope is determined per implementation.
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>

    {/* Family detail sections */}
    {PORTFOLIO.map((f, i) => (
      <FamilySection key={f.id} f={f} index={i} />
    ))}

    {/* Market & Integration Note */}
    <SectionWrapper id="portfolio-note" alt>
      <div className="border-l-2 border-cyan-500 pl-6 max-w-4xl">
        <div className="tag-overline">Market and integration note</div>
        <div
          data-testid="portfolio-market-note"
          className="mt-3 space-y-4 text-[15.5px] sm:text-base text-ink-700 leading-relaxed"
        >
          <p>
            Allinstra’s product portfolio is modular and implementation-aware. Not every
            module, integration, or public-system connection is automatically available in
            every country or institution.
          </p>
          <p>
            Scope, availability, and implementation sequence are evaluated according to local
            legislation, public infrastructure, third-party access, technical feasibility,
            institutional priorities, and phased implementation needs.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* Final CTA */}
    <SectionWrapper id="portfolio-final-cta">
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="tag-overline">Final step</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              Build the right institutional system structure.
            </h2>
            <p className="mt-3 text-[15px] text-ink-600 leading-relaxed max-w-2xl">
              Allinstra helps institutions evaluate the right combination of modules,
              integrations, workflows, and implementation steps according to their operational
              needs and local realities.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link
              to="/contact"
              data-testid="portfolio-final-discuss"
              className="btn-primary"
            >
              Discuss Your Institution <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="portfolio-final-contact"
              className="btn-secondary"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default ProductPortfolio;
